/**
 * Collection infinite scroll — loads additional paginated product pages
 * and appends them to #product-grid without showing pagination controls.
 */
(function () {
  const SENTINEL_SELECTOR = '[data-collection-infinite-scroll]';
  let observer = null;
  let loading = false;

  function getSentinel() {
    return document.querySelector(SENTINEL_SELECTOR);
  }

  function getProductGrid() {
    return document.getElementById('product-grid');
  }

  function buildPageUrl(nextUrl) {
    if (!nextUrl) return null;

    const url = new URL(nextUrl, window.location.origin);
    const sectionId = getProductGrid()?.dataset?.id;
    if (sectionId) url.searchParams.set('section_id', sectionId);

    // Preserve current filter/sort params from the live URL, except page/section_id.
    const current = new URLSearchParams(window.location.search);
    current.forEach((value, key) => {
      if (key === 'page' || key === 'section_id') return;
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });

    return url.toString();
  }

  function stopObserving() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  function finish(sentinel) {
    if (sentinel) sentinel.remove();
    stopObserving();
  }

  async function loadNextPage(sentinel) {
    if (loading || !sentinel) return;

    const nextUrl = sentinel.dataset.nextUrl;
    if (!nextUrl) {
      finish(sentinel);
      return;
    }

    const grid = getProductGrid();
    if (!grid) {
      finish(sentinel);
      return;
    }

    loading = true;
    sentinel.classList.add('is-loading');
    sentinel.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(buildPageUrl(nextUrl));
      if (!response.ok) throw new Error('Failed to load next page');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.getElementById('product-grid');
      const nextSentinel = doc.querySelector(SENTINEL_SELECTOR);

      if (nextGrid) {
        Array.from(nextGrid.children).forEach((item) => {
          grid.appendChild(item);

          // Keep OR-filter product cache in sync when more products load.
          if (window.FacetFiltersForm?.orFilterProductCache) {
            FacetFiltersForm.orFilterProductCache.push(item.cloneNode(true));
          }
        });
      }

      document.dispatchEvent(new CustomEvent('collection:products-appended'));

      if (nextSentinel?.dataset?.nextUrl) {
        sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
        sentinel.classList.remove('is-loading');
        sentinel.setAttribute('aria-busy', 'false');
      } else {
        finish(sentinel);
      }
    } catch (error) {
      console.warn('Collection infinite scroll failed', error);
      sentinel.classList.remove('is-loading');
      sentinel.setAttribute('aria-busy', 'false');
    } finally {
      loading = false;
    }
  }

  function init() {
    stopObserving();
    loading = false;

    const sentinel = getSentinel();
    if (!sentinel || !sentinel.dataset.nextUrl) {
      if (sentinel && !sentinel.dataset.nextUrl) finish(sentinel);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: load remaining pages sequentially.
      (async function loadAll() {
        while (getSentinel()?.dataset?.nextUrl) {
          await loadNextPage(getSentinel());
        }
      })();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadNextPage(entry.target);
        });
      },
      {
        rootMargin: '600px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  // Re-init after Dawn facet AJAX replaces the product grid.
  const originalRender =
    window.FacetFiltersForm && FacetFiltersForm.renderProductGridContainer
      ? FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm)
      : null;

  function patchFacets() {
    if (!window.FacetFiltersForm || typeof FacetFiltersForm.renderProductGridContainer !== 'function') {
      return false;
    }
    if (FacetFiltersForm.__infiniteScrollPatched) return true;

    const prev = FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm);
    FacetFiltersForm.renderProductGridContainer = function (html) {
      prev(html);
      init();
    };
    FacetFiltersForm.__infiniteScrollPatched = true;
    return true;
  }

  if (!patchFacets()) {
    document.addEventListener('DOMContentLoaded', patchFacets);
    // facets.js may define FacetFiltersForm after this file.
    setTimeout(patchFacets, 0);
    setTimeout(patchFacets, 500);
  }

  // Expose for manual re-init if needed.
  window.CollectionInfiniteScroll = { init };
})();
